// Favorites ("My Visit" list) — persisted per exhibition in localStorage.
// Components stay in sync through a window event, so a heart toggled on a
// list row updates the navbar badge and the favorites page immediately.
import { useState, useEffect, useCallback } from 'react';
import { storageService } from './storageService';

const EVENT = 'favorites-changed';
const keyFor = (exhibitionId) => `favorites_list_${exhibitionId || 'global'}`;

const notify = () => window.dispatchEvent(new CustomEvent(EVENT));

// IDs arrive as numbers from the API but strings from route params
const sameId = (a, b) => String(a) === String(b);

export const favoritesService = {
  getAll(exhibitionId) {
    const list = storageService.getItem(keyFor(exhibitionId), []);
    return Array.isArray(list) ? list : [];
  },

  isFavorite(exhibitionId, activityId) {
    return this.getAll(exhibitionId).some((f) => sameId(f.ActivityID, activityId));
  },

  /** Adds or removes an exhibitor. Returns true if it is now a favorite. */
  toggle(exhibitionId, exhibitor) {
    const list = this.getAll(exhibitionId);
    const exists = list.some((f) => sameId(f.ActivityID, exhibitor.ActivityID));
    const next = exists
      ? list.filter((f) => !sameId(f.ActivityID, exhibitor.ActivityID))
      : [
          ...list,
          {
            // store just enough to render the favorites page offline
            ActivityID: exhibitor.ActivityID,
            EponCatEng: exhibitor.EponCatEng || null,
            EponCatGr: exhibitor.EponCatGr || null,
            CityEng: exhibitor.CityEng || null,
            CityGr: exhibitor.CityGr || null,
            PGR: exhibitor.PGR || null,
            PENG: exhibitor.PENG || null,
            logoURL: exhibitor.logoURL || null,
            savedAt: Date.now(),
          },
        ];
    storageService.setItem(keyFor(exhibitionId), next);
    notify();
    return !exists;
  },

  remove(exhibitionId, activityId) {
    const next = this.getAll(exhibitionId).filter((f) => !sameId(f.ActivityID, activityId));
    storageService.setItem(keyFor(exhibitionId), next);
    notify();
  },
};

/** Live favorites list for an exhibition; re-renders on any change. */
export function useFavorites(exhibitionId) {
  const [favorites, setFavorites] = useState(() => favoritesService.getAll(exhibitionId));

  useEffect(() => {
    const refresh = () => setFavorites(favoritesService.getAll(exhibitionId));
    refresh();
    window.addEventListener(EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [exhibitionId]);

  const toggle = useCallback(
    (exhibitor) => favoritesService.toggle(exhibitionId, exhibitor),
    [exhibitionId]
  );
  const remove = useCallback(
    (activityId) => favoritesService.remove(exhibitionId, activityId),
    [exhibitionId]
  );
  const isFavorite = useCallback(
    (activityId) => favorites.some((f) => sameId(f.ActivityID, activityId)),
    [favorites]
  );

  return { favorites, toggle, remove, isFavorite };
}
